import React, { useState } from 'react';
import { CheckCircleIcon, XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { getDisplayName } from '../utils/firestore';

export default function UserVerificationModal({ user, onClose, onApprove, onReject, onRequestInfo }) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [firstEquityInfoRequest, setFirstEquityInfoRequest] = useState('');
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [showInfoRequestForm, setShowInfoRequestForm] = useState(false);
  const [verificationChecklist, setVerificationChecklist] = useState({
    identityVerified: false,
    addressVerified: false,
    employmentVerified: false,
    incomeVerified: false
  });

  const handleChecklistChange = (item) => {
    setVerificationChecklist(prev => ({
      ...prev,
      [item]: !prev[item]
    }));
  };

  const handleApprove = () => {
    onApprove(user.uid);
    onClose();
  };

  const handleReject = () => {
    if (rejectionReason.trim()) {
      onReject(user.uid, rejectionReason);
      onClose();
    }
  };

  const handleRequestInfo = () => {
    if (firstEquityInfoRequest.trim()) {
      onRequestInfo(user.uid, firstEquityInfoRequest);
      onClose();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to format address
  const formatAddress = (address) => {
    if (!address) return 'No address provided';
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.city || address.state) {
      const cityState = [address.city, address.state].filter(Boolean).join(', ');
      if (cityState) parts.push(cityState);
    }
    if (address.postalCode) parts.push(address.postalCode);
    if (address.country) parts.push(address.country);
    return parts.join('\n') || 'No address provided';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Verify User</h2>
          <p className="text-sm text-gray-500 mt-1">
            Review and verify user information
          </p>
        </div>

        <div className="p-6">
          <div className="space-y-6">
            {/* User Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">User Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium">{getDisplayName(user) || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{user.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{user.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium">
                    {user.address?.street}<br />
                    {user.address?.city}, {user.address?.state} {user.address?.zipCode}
                  </p>
                </div>
              </div>
            </div>

            {/* Verification Checklist */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Verification Checklist</h3>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={verificationChecklist.identityVerified}
                    onChange={() => handleChecklistChange('identityVerified')}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                  />
                  <span className="ml-2">Identity Verified</span>
                </label>
                <label className="flex items-center">
                        <input
                          type="checkbox"
                    checked={verificationChecklist.addressVerified}
                    onChange={() => handleChecklistChange('addressVerified')}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                  <span className="ml-2">Address Verified</span>
                      </label>
                <label className="flex items-center">
                        <input
                          type="checkbox"
                    checked={verificationChecklist.employmentVerified}
                    onChange={() => handleChecklistChange('employmentVerified')}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                  <span className="ml-2">Employment Verified</span>
                      </label>
                <label className="flex items-center">
                        <input
                          type="checkbox"
                    checked={verificationChecklist.incomeVerified}
                    onChange={() => handleChecklistChange('incomeVerified')}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                  <span className="ml-2">Income Verified</span>
                      </label>
              </div>
            </div>

            {/* Rejection Form */}
            {showRejectionForm && (
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-medium text-red-900 mb-2">Rejection Reason</h4>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full p-2 border border-red-300 rounded-lg focus:ring-red-500 focus:border-red-500"
                  rows="3"
                  placeholder="Enter reason for rejection..."
                />
              </div>
            )}

            {/* Information Request Form */}
            {showInfoRequestForm && (
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-medium text-yellow-900 mb-2">Request Additional Information</h4>
                <textarea
                  value={firstEquityInfoRequest}
                  onChange={(e) => setFirstEquityInfoRequest(e.target.value)}
                  className="w-full p-2 border border-yellow-300 rounded-lg focus:ring-yellow-500 focus:border-yellow-500"
                  rows="3"
                  placeholder="Enter information request details..."
                />
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={() => setShowInfoRequestForm(true)}
            className="px-4 py-2 text-yellow-700 bg-yellow-100 hover:bg-yellow-200 rounded-lg"
          >
            Request Info
          </button>
          <button
            onClick={() => setShowRejectionForm(true)}
            className="px-4 py-2 text-red-700 bg-red-100 hover:bg-red-200 rounded-lg"
          >
            Reject
          </button>
          <button
            onClick={handleApprove}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  );
} 